import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Button } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { JourneyEvent, JourneySeverity } from '../client-journey.model';
import { ClientContactService } from '../../client-contact/client-contact.service';
import { ClientSessionService } from '@brighthub/uisession';

const CHANNEL_ICONS: Record<string, string> = {
  email:  'pi pi-envelope',
  sms:    'pi pi-comment',
  phone:  'pi pi-phone',
  chat:   'pi pi-comments',
  system: 'pi pi-cog',
  crm:    'pi pi-briefcase',
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  email:                    'Email',
  sms:                      'SMS',
  call:                     'Call',
  chat:                     'Chat',
  opportunity_created:      'Opportunity Created',
  opportunity_updated:      'Opportunity Updated',
  opportunity_committed:    'Opportunity Committed',
  opportunity_completed:    'Opportunity Completed',
  opportunity_withdrawn:    'Opportunity Withdrawn',
  action_created:           'Action Created',
  action_updated:           'Action Updated',
  action_completed:         'Action Completed',
  action_blocked:           'Action Blocked',
  action_withdrawn:         'Action Withdrawn',
  outcome_detected:         'Outcome Detected',
  escalation:               'Escalation',
  handoff:                  'Handoff',
  financial_milestone:      'Financial Milestone',
  system:                   'System Event',
};

const SEVERITY_LABELS: Record<JourneySeverity, string> = {
  info:    'Info',
  warn:    'Warning',
  danger:  'Critical',
  success: 'Success',
};

const SEVERITY_TAG_SEVERITY: Record<JourneySeverity, string> = {
  info:    'info',
  warn:    'warn',
  danger:  'danger',
  success: 'success',
};

@Component({
  selector: 'bai-journey-event-detail-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button, TagModule],
  template: `
    <div class="bai-journey-detail" role="region" aria-label="Journey event details">

      <!-- Section 1: Event header -->
      <div class="bai-jd-header">
        <div class="bai-jd-header-row">
          <span class="bai-jd-channel-icon" [attr.aria-label]="event.channel">
            <i [class]="channelIcon(event.channel)"></i>
          </span>
          <div class="bai-jd-header-meta">
            <span class="bai-jd-event-type-label">{{ eventTypeLabel(event.eventType) }}</span>
            @if (event.severity) {
              <p-tag
                [value]="severityLabel(event.severity)"
                [severity]="tagSeverity(event.severity)"
                styleClass="bai-jd-severity-tag"
              />
            }
          </div>
        </div>
        <h2 class="bai-jd-title">{{ event.title }}</h2>
        <div class="bai-jd-timestamps">
          <span><i class="pi pi-clock mr-1"></i>{{ formatDateTime(event.occurredAt) }}</span>
          @if (event.direction) {
            <span class="bai-jd-direction">
              <i [class]="event.direction === 'inbound' ? 'pi pi-arrow-down' : 'pi pi-arrow-up'"></i>
              {{ event.direction === 'inbound' ? 'Inbound' : 'Outbound' }}
            </span>
          }
        </div>
      </div>

      <!-- Section 2: Summary -->
      <div class="bai-jd-section">
        <h3 class="bai-jd-section-title">Summary</h3>
        <p class="bai-jd-summary-text">{{ event.summary }}</p>
      </div>

      <!-- Section 3: Transcript / content -->
      @if (event.detailPayload.transcript) {
        <div class="bai-jd-section">
          <h3 class="bai-jd-section-title">
            <i class="pi pi-align-left mr-1"></i>Transcript / Content
          </h3>
          <pre class="bai-jd-transcript">{{ event.detailPayload.transcript }}</pre>
        </div>
      }

      <!-- Section 4: Actor chain -->
      @if (event.detailPayload.actorChain && event.detailPayload.actorChain.length > 0) {
        <div class="bai-jd-section">
          <h3 class="bai-jd-section-title">
            <i class="pi pi-users mr-1"></i>Actor Chain
          </h3>
          <div class="bai-jd-actor-chain" role="list" aria-label="Actor chain">
            @for (actor of event.detailPayload.actorChain; track $index) {
              <span class="bai-jd-actor" role="listitem">{{ actor }}</span>
              @if (!$last) {
                <i class="pi pi-arrow-right bai-jd-actor-arrow" aria-hidden="true"></i>
              }
            }
          </div>
        </div>
      }

      <!-- Section 5: Journey intelligence -->
      @if (hasIntelligence()) {
        <div class="bai-jd-section">
          <h3 class="bai-jd-section-title">
            <i class="pi pi-lightbulb mr-1"></i>Journey Intelligence
          </h3>
          <div class="bai-jd-intelligence-grid">
            @if (event.detailPayload.confidence !== undefined) {
              <div class="bai-jd-intel-item">
                <span class="bai-jd-intel-label">AI Confidence</span>
                <span class="bai-jd-intel-value" [class.text-green-600]="event.detailPayload.confidence >= 0.8" [class.text-amber-600]="event.detailPayload.confidence < 0.8 && event.detailPayload.confidence >= 0.6" [class.text-red-600]="event.detailPayload.confidence < 0.6">
                  {{ formatConfidence(event.detailPayload.confidence) }}
                </span>
              </div>
            }
            @if (event.detailPayload.goalAligned !== undefined) {
              <div class="bai-jd-intel-item">
                <span class="bai-jd-intel-label">Goal Aligned</span>
                <span class="bai-jd-intel-value" [class.text-green-600]="event.detailPayload.goalAligned" [class.text-red-600]="!event.detailPayload.goalAligned">
                  <i [class]="event.detailPayload.goalAligned ? 'pi pi-check-circle' : 'pi pi-times-circle'"></i>
                  {{ event.detailPayload.goalAligned ? 'Yes' : 'No' }}
                </span>
              </div>
            }
            @if (event.detailPayload.escalationReason) {
              <div class="bai-jd-intel-item bai-jd-intel-full">
                <span class="bai-jd-intel-label">Escalation Reason</span>
                <span class="bai-jd-intel-value text-red-700">{{ event.detailPayload.escalationReason }}</span>
              </div>
            }
            @if (event.detailPayload.metricSnippet) {
              <div class="bai-jd-intel-item bai-jd-intel-full">
                <span class="bai-jd-intel-label">Key Metric</span>
                <span class="bai-jd-intel-value font-semibold">{{ event.detailPayload.metricSnippet }}</span>
              </div>
            }
            @if (event.detailPayload.notes) {
              <div class="bai-jd-intel-item bai-jd-intel-full">
                <span class="bai-jd-intel-label">Notes</span>
                <span class="bai-jd-intel-value">{{ event.detailPayload.notes }}</span>
              </div>
            }
          </div>
        </div>
      }

      <!-- Section 6: Linked entities -->
      @if (hasLinkedEntities()) {
        <div class="bai-jd-section">
          <h3 class="bai-jd-section-title">
            <i class="pi pi-link mr-1"></i>Linked Entities
          </h3>
          @if (event.detailPayload.linkedOpportunityTitles && event.detailPayload.linkedOpportunityTitles.length > 0) {
            <div class="bai-jd-linked-group">
              <span class="bai-jd-linked-label">Opportunities</span>
              @for (title of event.detailPayload.linkedOpportunityTitles; track $index) {
                <span class="bai-jd-linked-item"><i class="pi pi-star-fill mr-1 text-purple-500"></i>{{ title }}</span>
              }
            </div>
          }
          @if (event.detailPayload.linkedActionTitles && event.detailPayload.linkedActionTitles.length > 0) {
            <div class="bai-jd-linked-group">
              <span class="bai-jd-linked-label">Actions</span>
              @for (title of event.detailPayload.linkedActionTitles; track $index) {
                <span class="bai-jd-linked-item"><i class="pi pi-check-square mr-1 text-indigo-500"></i>{{ title }}</span>
              }
            </div>
          }
          @if (event.detailPayload.linkedOutcomeTitles && event.detailPayload.linkedOutcomeTitles.length > 0) {
            <div class="bai-jd-linked-group">
              <span class="bai-jd-linked-label">Outcomes</span>
              @for (title of event.detailPayload.linkedOutcomeTitles; track $index) {
                <span class="bai-jd-linked-item"><i class="pi pi-flag-fill mr-1 text-green-500"></i>{{ title }}</span>
              }
            </div>
          }
        </div>
      }

      <!-- Section 7: Audit metadata -->
      <div class="bai-jd-section bai-jd-section-audit">
        <h3 class="bai-jd-section-title">
          <i class="pi pi-database mr-1"></i>Audit Metadata
        </h3>
        <div class="bai-jd-audit-grid">
          <span class="bai-jd-audit-label">Event ID</span>
          <span class="bai-jd-audit-value font-mono text-xs">{{ event.id }}</span>
          <span class="bai-jd-audit-label">Event Type</span>
          <span class="bai-jd-audit-value font-mono text-xs">{{ event.eventType }}</span>
          <span class="bai-jd-audit-label">Source Type</span>
          <span class="bai-jd-audit-value">{{ event.sourceType }}</span>
          <span class="bai-jd-audit-label">Source</span>
          <span class="bai-jd-audit-value">{{ event.sourceName }}</span>
          <span class="bai-jd-audit-label">Channel</span>
          <span class="bai-jd-audit-value">{{ event.channel }}</span>
          <span class="bai-jd-audit-label">Created</span>
          <span class="bai-jd-audit-value">{{ formatDateTime(event.createdAt) }}</span>
          <span class="bai-jd-audit-label">Updated</span>
          <span class="bai-jd-audit-value">{{ formatDateTime(event.updatedAt) }}</span>
        </div>
      </div>

      <!-- Tags -->
      @if (event.tags.length > 0) {
        <div class="bai-jd-tags-row">
          @for (tag of event.tags; track $index) {
            <span class="bai-jd-tag-pill">{{ tag }}</span>
          }
        </div>
      }

      <!-- Footer: contact CTAs + close -->
      <div class="bai-jd-footer">
        @if (event.channel === 'phone' || event.channel === 'sms') {
          <p-button label="Call client" icon="pi pi-phone" size="small" severity="secondary" (onClick)="callClient()" />
        }
        @if (event.channel === 'email') {
          <p-button label="Email client" icon="pi pi-envelope" size="small" severity="secondary" (onClick)="emailClient()" />
        }
        <p-button label="Close" icon="pi pi-times" severity="secondary" (onClick)="close()" />
      </div>
    </div>
  `,
  styles: [`
    .bai-journey-detail {
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0;
      max-height: 80vh;
      overflow-y: auto;
    }
    .bai-jd-header {
      padding: 1.25rem 1.5rem 1rem;
      border-bottom: 1px solid var(--surface-border, #e5e7eb);
      background: var(--surface-50, #f9fafb);
    }
    .bai-jd-header-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.5rem;
    }
    .bai-jd-channel-icon {
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--primary-100, #dbeafe);
      border-radius: 50%;
      color: var(--primary-600, #2563eb);
      font-size: 0.875rem;
    }
    .bai-jd-header-meta {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    .bai-jd-event-type-label {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-color-secondary, #6b7280);
    }
    .bai-jd-title {
      font-size: 1rem;
      font-weight: 700;
      color: var(--text-color, #111827);
      margin: 0.25rem 0;
    }
    .bai-jd-timestamps {
      display: flex;
      gap: 1rem;
      font-size: 0.75rem;
      color: var(--text-color-secondary, #6b7280);
      flex-wrap: wrap;
    }
    .bai-jd-direction {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    .bai-jd-section {
      padding: 1rem 1.5rem;
      border-bottom: 1px solid var(--surface-border, #e5e7eb);
    }
    .bai-jd-section-title {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-color-secondary, #6b7280);
      margin: 0 0 0.5rem;
      display: flex;
      align-items: center;
    }
    .bai-jd-summary-text {
      font-size: 0.875rem;
      color: var(--text-color, #374151);
      line-height: 1.6;
      margin: 0;
    }
    .bai-jd-transcript {
      font-size: 0.8rem;
      font-family: inherit;
      white-space: pre-wrap;
      background: var(--surface-100, #f3f4f6);
      border: 1px solid var(--surface-border, #e5e7eb);
      border-radius: 0.375rem;
      padding: 0.75rem;
      color: var(--text-color, #374151);
      line-height: 1.6;
      margin: 0;
    }
    .bai-jd-actor-chain {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.375rem;
    }
    .bai-jd-actor {
      background: var(--primary-50, #eff6ff);
      color: var(--primary-700, #1d4ed8);
      border: 1px solid var(--primary-200, #bfdbfe);
      border-radius: 1rem;
      padding: 0.2rem 0.6rem;
      font-size: 0.75rem;
      font-weight: 500;
    }
    .bai-jd-actor-arrow {
      color: var(--text-color-secondary, #9ca3af);
      font-size: 0.625rem;
    }
    .bai-jd-intelligence-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
    }
    .bai-jd-intel-item {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .bai-jd-intel-full {
      grid-column: 1 / -1;
    }
    .bai-jd-intel-label {
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .bai-jd-intel-value {
      font-size: 0.8rem;
      color: var(--text-color, #374151);
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    .bai-jd-linked-group {
      margin-bottom: 0.5rem;
    }
    .bai-jd-linked-label {
      display: block;
      font-size: 0.7rem;
      font-weight: 600;
      color: var(--text-color-secondary, #6b7280);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 0.25rem;
    }
    .bai-jd-linked-item {
      display: flex;
      align-items: center;
      font-size: 0.8rem;
      color: var(--text-color, #374151);
      padding: 0.25rem 0;
    }
    .bai-jd-section-audit {
      background: var(--surface-50, #f9fafb);
    }
    .bai-jd-audit-grid {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.25rem 1rem;
      font-size: 0.75rem;
    }
    .bai-jd-audit-label {
      color: var(--text-color-secondary, #6b7280);
      font-weight: 500;
      white-space: nowrap;
    }
    .bai-jd-audit-value {
      color: var(--text-color, #374151);
      word-break: break-all;
    }
    .bai-jd-tags-row {
      padding: 0.75rem 1.5rem;
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      border-bottom: 1px solid var(--surface-border, #e5e7eb);
    }
    .bai-jd-tag-pill {
      background: var(--surface-200, #e5e7eb);
      color: var(--text-color-secondary, #374151);
      border-radius: 1rem;
      padding: 0.15rem 0.5rem;
      font-size: 0.7rem;
      font-weight: 500;
    }
    .bai-jd-footer {
      padding: 0.75rem 1.5rem;
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
    }
  `],
})
export class JourneyEventDetailDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly _contactService = inject(ClientContactService);
  private readonly _sessionService = inject(ClientSessionService);

  readonly event: JourneyEvent = this.config.data.event;

  channelIcon(channel: string): string {
    return CHANNEL_ICONS[channel] ?? 'pi pi-circle';
  }

  eventTypeLabel(eventType: string): string {
    return EVENT_TYPE_LABELS[eventType] ?? eventType;
  }

  severityLabel(severity: JourneySeverity): string {
    return SEVERITY_LABELS[severity];
  }

  tagSeverity(severity: JourneySeverity): string {
    return SEVERITY_TAG_SEVERITY[severity];
  }

  formatDateTime(iso: string): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(iso));
  }

  formatConfidence(value: number): string {
    return new Intl.NumberFormat('en-US', { style: 'percent', maximumFractionDigits: 0 }).format(value);
  }

  hasIntelligence(): boolean {
    const p = this.event.detailPayload;
    return (
      p.confidence !== undefined ||
      p.goalAligned !== undefined ||
      !!p.escalationReason ||
      !!p.metricSnippet ||
      !!p.notes
    );
  }

  hasLinkedEntities(): boolean {
    const p = this.event.detailPayload;
    return (
      (p.linkedOpportunityTitles?.length ?? 0) > 0 ||
      (p.linkedActionTitles?.length ?? 0) > 0 ||
      (p.linkedOutcomeTitles?.length ?? 0) > 0
    );
  }

  callClient(): void {
    const subjectData = this._sessionService.selectedSession()?.subjectData;
    if (!subjectData) return;
    const contact = this._contactService.resolveContact(subjectData);
    this._contactService.openPhone(contact.primaryPhone, 'journey_modal', subjectData.guid);
  }

  emailClient(): void {
    const subjectData = this._sessionService.selectedSession()?.subjectData;
    if (!subjectData) return;
    const contact = this._contactService.resolveContact(subjectData);
    this._contactService.openEmail(contact.primaryEmail, 'journey_modal', subjectData.guid);
  }

  close(): void {
    this.ref.close();
  }
}
